if (!customElements.get('cinoton-category-carousel')) {
  customElements.define('cinoton-category-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-category-track]');
      this.previous = this.querySelector('[data-category-prev]');
      this.next = this.querySelector('[data-category-next]');
      if (!this.track || !this.previous || !this.next) return;

      this.updateControls = () => {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const overflow = maxScroll >= 2;
        this.previous.hidden = !overflow;
        this.next.hidden = !overflow;
        this.previous.setAttribute('aria-disabled', String(this.track.scrollLeft < 2));
        this.next.setAttribute('aria-disabled', String(this.track.scrollLeft >= maxScroll - 2));
        this.track.tabIndex = overflow ? 0 : -1;
      };
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.updateControls, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateControls);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.updateControls);
      }
      this.updateControls();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.previous.removeEventListener('click', this.onPrevious);
      this.next.removeEventListener('click', this.onNext);
      this.track.removeEventListener('scroll', this.updateControls);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.updateControls);
    }

    move(direction) {
      if ((direction < 0 ? this.previous : this.next).getAttribute('aria-disabled') === 'true') return;
      const card = this.track.querySelector('.cinoton-categories__card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior });
    }
  });
}

if (!customElements.get('cinoton-spaces-gallery')) {
  customElements.define('cinoton-spaces-gallery', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('.cinoton-spaces-gallery__play');
        if (!button || !this.contains(button)) return;
        const tile = button.closest('.cinoton-spaces-gallery__tile');
        const video = tile?.querySelector('video');
        if (!video) return;
        tile.classList.add('is-playing');
        video.controls = true;
        video.removeAttribute('tabindex');
        video.setAttribute('aria-label', button.getAttribute('aria-label') || 'CINOTON video');
        try {
          await video.play();
          video.focus();
        } catch {
          tile.classList.remove('is-playing');
          video.controls = false;
          video.setAttribute('tabindex', '-1');
          button.focus();
        }
      });
      this.addEventListener('ended', (event) => {
        const video = event.target;
        if (!(video instanceof HTMLVideoElement)) return;
        const tile = video.closest('.cinoton-spaces-gallery__tile');
        const restoreFocus = document.activeElement === video;
        tile?.classList.remove('is-playing');
        video.controls = false;
        video.setAttribute('tabindex', '-1');
        if (restoreFocus) tile?.querySelector('.cinoton-spaces-gallery__play')?.focus();
      }, true);
    }
  });
}

if (!customElements.get('cinoton-badge-carousel')) {
  customElements.define('cinoton-badge-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-badge-track]');
      this.items = this.track ? Array.from(this.track.querySelectorAll(':scope > [data-badge-item]')) : [];
      if (!this.track || this.items.length < 2 || this.dataset.autoplay !== 'true') return;

      this.interval = Math.max(2, Number(this.dataset.interval) || 4) * 1000;
      this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseReasons = new Set();
      this.clones = [];
      this.cloneRounds = 0;
      this.enhanced = true;

      this.onScroll = () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.normalize(), 120);
      };
      this.onPointerEnter = () => this.setPaused('hover', true);
      this.onPointerLeave = () => this.setPaused('hover', false);
      this.onFocusIn = () => {
        window.clearTimeout(this.focusTimer);
        this.setPaused('focus', true);
      };
      this.onFocusOut = () => {
        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.setPaused('focus', false);
        });
      };
      this.onTouchStart = () => {
        window.clearTimeout(this.touchTimer);
        this.setPaused('touch', true);
      };
      this.onTouchEnd = (event) => {
        window.clearTimeout(this.touchTimer);
        if (event.touches?.length) return;
        this.touchTimer = window.setTimeout(() => this.setPaused('touch', false), this.interval);
      };
      this.onVisibilityChange = () => this.setPaused('hidden', document.hidden);
      this.onMotionChange = () => this.setPaused('motion', this.motionQuery.matches);
      this.onBlockSelect = (event) => {
        const item = this.items.find((candidate) => candidate.dataset.blockId === event.detail?.blockId);
        if (!item) return;
        this.setPaused('editor', true);
        item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.motionQuery.addEventListener?.('change', this.onMotionChange);

      this.onResize = () => {
        if (this.settingUp) return;
        const required = this.requiredCloneRounds();
        if (required && required !== this.cloneRounds) this.setupClones();
        else this.measure(false);
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }
      this.setPaused('editor', this.designMode);
      this.setPaused('motion', this.motionQuery.matches);
      this.setPaused('hidden', document.hidden);
      requestAnimationFrame(() => this.setupClones());
    }

    disconnectedCallback() {
      if (!this.enhanced) return;
      window.clearTimeout(this.timer);
      window.clearTimeout(this.scrollTimer);
      window.clearTimeout(this.touchTimer);
      window.clearTimeout(this.focusTimer);
      this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('touchcancel', this.onTouchEnd);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.motionQuery?.removeEventListener?.('change', this.onMotionChange);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.clones?.forEach((clone) => clone.remove());
    }

    createClone(item) {
      const clone = item.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-badge-item');
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
      this.clones.push(clone);
      return clone;
    }

    sequenceWidth() {
      const first = this.items[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return (first.getBoundingClientRect().width + gap) * this.items.length;
    }

    requiredCloneRounds() {
      const width = this.sequenceWidth();
      if (!width) return 0;
      return Math.ceil(this.track.clientWidth / width) + 1;
    }

    setupClones() {
      const rounds = this.requiredCloneRounds();
      if (!rounds) return;
      this.settingUp = true;
      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.firstFollowingClone = null;
      this.cloneRounds = rounds;

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let round = 0; round < rounds; round += 1) {
        this.items.forEach((item) => before.append(this.createClone(item)));
        this.items.forEach((item) => {
          const clone = this.createClone(item);
          if (!this.firstFollowingClone) this.firstFollowingClone = clone;
          after.append(clone);
        });
      }
      this.track.prepend(before);
      this.track.append(after);
      this.settingUp = false;
      requestAnimationFrame(() => this.measure(true));
    }

    measure(center) {
      const firstOriginal = this.items[0];
      const firstFollowingClone = this.firstFollowingClone;
      if (!firstOriginal || !firstFollowingClone) return;
      const trackLeft = this.track.getBoundingClientRect().left;
      const currentScroll = this.track.scrollLeft;
      const originalLeft = firstOriginal.getBoundingClientRect().left - trackLeft + currentScroll;
      const followingLeft = firstFollowingClone.getBoundingClientRect().left - trackLeft + currentScroll;
      this.originalStart = originalLeft;
      this.loopWidth = followingLeft - originalLeft;
      if (!this.loopWidth) return;
      if (center) this.jumpTo(this.originalStart);
      this.ready = true;
      this.schedule();
    }

    normalize() {
      if (!this.ready || !this.loopWidth) return;
      const position = this.track.scrollLeft;
      const delta = position - this.originalStart;
      const edgeTolerance = 1;
      if (delta < -edgeTolerance || delta >= this.loopWidth - edgeTolerance) {
        let withinLoop = ((delta % this.loopWidth) + this.loopWidth) % this.loopWidth;
        if (withinLoop >= this.loopWidth - edgeTolerance) withinLoop = 0;
        this.jumpTo(this.originalStart + withinLoop);
      }
    }

    jumpTo(left) {
      const snapType = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left, behavior: 'auto' });
      this.track.offsetHeight;
      this.track.style.scrollSnapType = snapType;
    }

    advance() {
      if (!this.ready || this.pauseReasons.size) return;
      const first = this.items[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: step, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
      this.schedule();
    }

    setPaused(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.schedule();
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (!this.ready || this.pauseReasons.size) return;
      this.timer = window.setTimeout(() => this.advance(), this.interval);
    }
  });
}
